import { HSVColor, RGBColor } from '@/types/colors';

export function hexToRgb(color: string): RGBColor {
  let r = 0;
  let g = 0;
  let b = 0;

  // 3 digits
  if (color.length === 4) {
    r = Number('0x' + color[1] + color[1]);
    g = Number('0x' + color[2] + color[2]);
    b = Number('0x' + color[3] + color[3]);

    // 6 digits
  } else if (color.length === 7) {
    r = Number('0x' + color[1] + color[2]);
    g = Number('0x' + color[3] + color[4]);
    b = Number('0x' + color[5] + color[6]);
  }

  return {
    r,
    g,
    b,
  };
}

const componentToHex = (c: number) => {
  const hex = c.toString(16);
  return hex.length === 1 ? '0' + hex : hex;
};

export function rgbToHex(color: RGBColor): string {
  return '#' + componentToHex(color.r) + componentToHex(color.g) + componentToHex(color.b);
}

export const rgbArrayToHex = (color: number[]): string => {
  return rgbToHex({ r: color[0], g: color[1], b: color[2] });
};

export const rgbaObjectToCss = (color: RGBColor | undefined | null): string => {
  if (!color) {
    return 'rgba(0, 0, 0, 1)';
  }

  return `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a ?? 1})`;
};

// between 0 and 1
export const normalizeRgbColor = (color?: RGBColor): RGBColor | undefined => {
  return color
    ? {
        r: color.r / 255,
        g: color.g / 255,
        b: color.b / 255,
        a: color.a,
      }
    : undefined;
};

export const restoreRgbColor = (color?: RGBColor): RGBColor | undefined => {
  return color
    ? {
        r: Math.round(color.r * 255),
        g: Math.round(color.g * 255),
        b: Math.round(color.b * 255),
        a: color.a,
      }
    : undefined;
};

/**
 * Hex to HSV. The picker holds HSV because saturation and value are the two axes of its
 * square and hue is its rail; hex is what every consumer of ColorField speaks.
 */
export function hexToHsv(color: string): HSVColor {
  const { r, g, b } = hexToRgb(color);
  const red = r / 255;
  const green = g / 255;
  const blue = b / 255;

  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const delta = max - min;

  let h = 0;
  if (delta !== 0) {
    if (max === red) {
      h = 60 * (((green - blue) / delta) % 6);
    } else if (max === green) {
      h = 60 * ((blue - red) / delta + 2);
    } else {
      h = 60 * ((red - green) / delta + 4);
    }
  }

  return {
    h: h < 0 ? h + 360 : h,
    s: max === 0 ? 0 : delta / max,
    v: max,
  };
}

/** HSV back to hex. Inverse of hexToHsv, so a drag round-trips without drifting. */
export function hsvToHex({ h, s, v }: HSVColor): string {
  const hue = ((h % 360) + 360) % 360;
  const chroma = v * s;
  const x = chroma * (1 - Math.abs(((hue / 60) % 2) - 1));
  const m = v - chroma;

  let rgb: [number, number, number];
  if (hue < 60) {
    rgb = [chroma, x, 0];
  } else if (hue < 120) {
    rgb = [x, chroma, 0];
  } else if (hue < 180) {
    rgb = [0, chroma, x];
  } else if (hue < 240) {
    rgb = [0, x, chroma];
  } else if (hue < 300) {
    rgb = [x, 0, chroma];
  } else {
    rgb = [chroma, 0, x];
  }

  return rgbToHex({
    r: Math.round((rgb[0] + m) * 255),
    g: Math.round((rgb[1] + m) * 255),
    b: Math.round((rgb[2] + m) * 255),
  });
}

// WCAG's sRGB linearisation. Below this luminance white text out-contrasts black; above it,
// black wins. 0.179 is the crossover, where both sit at a 4.58 ratio.
const DARK_LUMINANCE_LIMIT = 0.179;
const LINEAR_KNEE = 0.04045;

const linearize = (component: number) => {
  const channel = component / 255;
  return channel <= LINEAR_KNEE ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
};

/**
 * Whether a light ink reads better than a dark one on this colour. Used to keep an icon
 * legible on a swatch filled with a colour the user chose, which can be anything.
 *
 * Luminance, not HSV value: green carries most of the perceived brightness, so `#FFD717`
 * is light and `#0000FF` is dark even though both sit at full value.
 */
export const isDarkColor = (color: string): boolean => {
  const { r, g, b } = hexToRgb(color);

  return 0.2126 * linearize(r) + 0.7152 * linearize(g) + 0.0722 * linearize(b) < DARK_LUMINANCE_LIMIT;
};
